import { supabase } from '@/lib/supabaseClient';
import type { OrderItem, Order, Profile } from '@/types';
import { getOrderStatusEmailTemplate } from '@/emails/orderStatusTemplates';
import { getTemplate } from '@/lib/emailTemplateUtils';
import { sendOrderStatusEmail as sendUnifiedEmail } from '@/emails/unifiedEmailService';

// Helper function to get full order details
export async function getOrder(orderId: string) {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      *,
      profiles:profiles(*),
      order_items:order_items(
        *,
        product:products(*)
      ),
      order_history:order_history(*)
    `)
    .eq('id', orderId)
    .single();

  if (error) {
    console.error('Error fetching order:', error);
    throw error;
  }

  return data;
}

// Get all orders (for admin or client)
export async function getOrders(limit = 100, offset = 0) {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      id,
      display_id,
      created_at,
      status,
      total_amount,
      profiles:profiles(id, first_name, last_name, email, address)
    `)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }

  return data;
}

// Create a new order from cart
export async function createOrder(
  p_address: string,
  p_customer_id: string | null,
  p_delivery_date: string | null,
  p_delivery_notes: string | null,
  p_delivery_time: string | null,
  p_guest_details: any | null,
  p_guest_email: string | null,
  p_items: {product_id: string, quantity: number, unit_price?: number}[],
  p_total_amount: number
) {
  try {
    // Skip RPC attempts and directly use the table operations method
    console.log('Using direct order creation method for reliability');
    return createOrderDirect(
      p_address,
      p_customer_id,
      p_delivery_date,
      p_delivery_notes,
      p_delivery_time,
      p_guest_details,
      p_guest_email,
      p_items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price || 0
      })),
      p_total_amount
    );
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
}

// Test if the create_order function exists and is accessible
export async function testCreateOrderFunction() {
  try {
    // Simplest test possible - just check if the RPC method exists
    const { error } = await supabase.rpc('create_order', {}, { 
      head: true, // Don't execute, just check if it exists
      count: 'exact'
    });
    
    // 42883 is PostgreSQL's "function does not exist" error
    if (error && error.code === '42883') {
      console.log('create_order function does not exist');
      return { exists: false, error: error.message };
    }
    
    // If we get here, either the function exists (regardless of parameter count),
    // or we got some other error which we'll interpret as "function exists but
    // called incorrectly" - in both cases we'll try to use the function
    console.log('create_order function appears to exist');
    return { exists: true, error: null };
  } catch (error: any) {
    console.error('Error testing function existence:', error);
    // Default to saying it doesn't exist if we can't determine
    return { exists: false, error: error.message };
  }
}

// Create a new order from cart using direct table operations (fallback when RPC fails)
export async function createOrderDirect(
  p_address: string,
  p_customer_id: string | null,
  p_delivery_date: string | null,
  p_delivery_notes: string | null,
  p_delivery_time: string | null,
  p_guest_details: any | null,
  p_guest_email: string | null,
  p_items: {product_id: string, quantity: number, unit_price: number}[],
  p_total_amount: number
) {
  try {
    console.log('Creating order directly with total:', p_total_amount);
    console.log('Order items:', p_items);
    
    // Validate required fields
    if (!p_address) {
      throw new Error('Leveransadress krävs');
    }
    if (!p_items || p_items.length === 0) {
      throw new Error('Kundvagnen är tom');
    }
    if (!p_guest_email && !p_customer_id) {
      throw new Error('E-postadress krävs');
    }

    // Check inventory levels before proceeding
    for (const item of p_items) {
      try {
        console.log('Checking stock for product:', item.product_id);
        
        if (!item.product_id) {
          console.error('Invalid product ID:', item);
          throw new Error('En eller flera produkter är ogiltiga');
        }

        // First check if the product exists and get its details
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('id, name, price, stock_level')
          .eq('id', item.product_id)
          .maybeSingle();

        // Log the full response for debugging
        console.log('Product query response:', {
          data: productData,
          error: productError,
          productId: item.product_id
        });

        if (productError) {
          console.error('Database error checking product:', {
            error: productError,
            code: productError.code,
            message: productError.message,
            details: productError.details,
            hint: productError.hint
          });
          
          // Check for specific error types
          if (productError.code === 'PGRST116') {
            throw new Error(`Produkten kunde inte hittas (${item.product_id})`);
          } else if (productError.code === '42703') {
            throw new Error('Systemfel: Lagersaldo kan inte kontrolleras. Kontakta support.');
          } else if (productError.code?.startsWith('42')) {
            throw new Error('Systemfel: Databasfel. Kontakta support.');
          } else if (productError.code?.startsWith('28')) {
            throw new Error('Systemfel: Behörighetsfel. Kontakta support.');
          }
          
          throw new Error('Kunde inte kontrollera lagersaldo. Vänligen försök igen.');
        }

        if (!productData) {
          console.error('Product not found:', {
            productId: item.product_id,
            searchResult: productData
          });
          throw new Error(`Produkten kunde inte hittas (${item.product_id})`);
        }

        // Log the product data
        console.log('Found product:', {
          id: productData.id,
          name: productData.name,
          stock_level: productData.stock_level,
          requestedQuantity: item.quantity
        });

        // Check if stock_level field exists
        if (!('stock_level' in productData)) {
          console.error('Product has no stock_level field:', productData);
          throw new Error('Systemfel: Lagersaldo saknas i databasen. Kontakta support.');
        }

        // Ensure stock_level is a number
        const stock_level = typeof productData.stock_level === 'number' ? 
          productData.stock_level : 
          parseInt(String(productData.stock_level));

        // Ensure quantity is a number
        const quantity = typeof item.quantity === 'number' ? 
          item.quantity : 
          parseInt(String(item.quantity));

        console.log('Inventory check:', {
          productId: item.product_id,
          productName: productData.name,
          availableStock: stock_level,
          requestedQuantity: quantity,
          stockLevelType: typeof productData.stock_level,
          quantityType: typeof item.quantity
        });

        if (isNaN(stock_level)) {
          console.error('Invalid stock_level value:', {
            productId: item.product_id,
            stock_level: productData.stock_level,
            type: typeof productData.stock_level
          });
          throw new Error(`Ogiltigt lagersaldo för ${productData.name}`);
        }

        if (isNaN(quantity) || quantity <= 0) {
          console.error('Invalid quantity:', {
            productId: item.product_id,
            quantity: item.quantity,
            type: typeof item.quantity
          });
          throw new Error(`Ogiltig kvantitet för ${productData.name}`);
        }

        // Check if we have enough stock
        if (stock_level < quantity) {
          console.error('Insufficient stock:', {
            productId: item.product_id,
            productName: productData.name,
            available: stock_level,
            requested: quantity
          });
          throw new Error(`Tyvärr finns det inte tillräckligt med ${productData.name} i lager (${stock_level} st tillgängliga)`);
        }

        console.log('Stock check passed for product:', {
          productId: item.product_id,
          productName: productData.name,
          available: stock_level,
          requested: quantity
        });
      } catch (error) {
        console.error('Error in stock check:', error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Kunde inte kontrollera lagersaldo. Vänligen försök igen.');
      }
    }
    
    // Force validate and normalize the total amount
    p_total_amount = Number(p_total_amount);
    
    // Validate the total amount
    if (isNaN(p_total_amount) || p_total_amount <= 0) {
      console.error('Invalid total amount:', p_total_amount);
      // Recalculate from items as a fallback
      p_total_amount = p_items.reduce((sum: number, item: any) => {
        const itemPrice = Number(item.unit_price) || 0;
        const itemQuantity = Number(item.quantity) || 0;
        return sum + (itemQuantity * itemPrice);
      }, 0);
      console.log('Recalculated total amount:', p_total_amount);
      
      if (p_total_amount <= 0) {
        throw new Error('Orderbeloppet måste vara större än 0');
      }
    }
    
    // Ensure the total is a valid number with 2 decimal places
    p_total_amount = Number(Number(p_total_amount).toFixed(2));
    console.log('Final validated total amount:', p_total_amount);
    
    // Create or update the customer profile if guest email is provided
    let customerId = p_customer_id;
    // Define a GuestDetails interface type
    interface GuestDetails {
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      address?: string;
    }
    
    let guestDetails: GuestDetails | null = null;
    
    if (p_guest_details) {
      try {
        const parsedDetails = typeof p_guest_details === 'string' 
          ? JSON.parse(p_guest_details) 
          : p_guest_details;
        
        // Validate guest details structure
        if (typeof parsedDetails !== 'object' || parsedDetails === null) {
          console.warn('Invalid guest details format, using null');
          guestDetails = null;
        } else {
          // Ensure all fields are strings and email is set
          guestDetails = {
            firstName: String(parsedDetails.firstName || ''),
            lastName: String(parsedDetails.lastName || ''),
            phone: String(parsedDetails.phone || ''),
            email: String(parsedDetails.email || p_guest_email || '')
          };
          
          // If we have guest details but no email, use the p_guest_email
          if (!guestDetails.email && p_guest_email) {
            guestDetails.email = p_guest_email;
          }
          
          console.log('Guest email debug info:', {
            from_guest_details: guestDetails.email,
            from_param: p_guest_email,
            final: guestDetails.email || p_guest_email
          });
        }
        
        console.log('Validated guest details:', guestDetails);
      } catch (error) {
        console.warn('Error parsing guest details:', error);
        // If we have a guest email but parsing failed, create minimal guest details
        if (p_guest_email) {
          guestDetails = {
            firstName: '',
            lastName: '',
            phone: '',
            email: p_guest_email
          };
          console.log('Created minimal guest details with email');
        } else {
          guestDetails = null;
        }
      }
    } else if (p_guest_email) {
      // If we have a guest email but no details, create minimal guest details
      guestDetails = {
        firstName: '',
        lastName: '',
        phone: '',
        email: p_guest_email
      };
      console.log('Created guest details from email only');
    }
    
    // Parse or format customer details for storage if we don't have a linked profile
    const formattedGuestDetails = (!customerId && guestDetails) 
      ? JSON.stringify(guestDetails)
      : null;
    
    // Debug log for order creation
    console.log('Order creation data:', {
      customer_id: customerId,
      guest_email: !customerId ? p_guest_email : null,
      guest_details: guestDetails,
      total_amount: p_total_amount,
      delivery_date: p_delivery_date,
      delivery_time: p_delivery_time
    });
    
    // Validate delivery date format if provided
    if (p_delivery_date) {
      try {
        const date = new Date(p_delivery_date);
        if (isNaN(date.getTime())) {
          throw new Error('Ogiltigt leveransdatum');
        }
      } catch (error) {
        throw new Error('Ogiltigt leveransdatum');
      }
    }
    
    // Validate delivery time format if provided
    if (p_delivery_time) {
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
      if (!timeRegex.test(p_delivery_time)) {
        throw new Error('Ogiltig leveranstid');
      }
    }
    
    // Start a transaction manually
    // 1. Create the order first
    console.log('Creating order with guest details:', {
      guest_email: !customerId ? (p_guest_email || (guestDetails && guestDetails.email) || '') : null,
      guest_details: formattedGuestDetails,
      has_customer_id: !!customerId
    });

    // Ensure we have a valid email to use for the order - extract from all possible sources
    let guestEmailForOrder = null;
    if (!customerId) {
      // For guest checkout, we need to ensure we have an email
      if (p_guest_email) {
        guestEmailForOrder = p_guest_email;
      } else if (guestDetails && guestDetails.email) {
        guestEmailForOrder = guestDetails.email;
      } else if (typeof formattedGuestDetails === 'string') {
        // Last resort: try to extract using regex
        try {
          const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
          const matches = formattedGuestDetails.match(emailRegex);
          if (matches && matches.length > 0) {
            guestEmailForOrder = matches[0];
            console.log('Extracted email from formattedGuestDetails using regex:', guestEmailForOrder);
          }
        } catch (e) {
          console.error('Error extracting email using regex:', e);
        }
      }
    }
    
    if (!customerId && !guestEmailForOrder) {
      console.error('No email available for guest order - this will likely cause email sending to fail', {
        p_guest_email,
        has_guest_details: !!guestDetails,
        guest_email_in_details: guestDetails ? guestDetails.email : null,
        customerId
      });
    } else {
      console.log('Using email for order:', guestEmailForOrder);
    }

    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .insert({
        customer_id: customerId,
        guest_email: guestEmailForOrder,
        guest_details: formattedGuestDetails,
        total_amount: p_total_amount,
        delivery_date: p_delivery_date,
        delivery_time: p_delivery_time,
        delivery_notes: p_delivery_notes?.trim() || null,
        address: p_address.trim(),
        status: 'pending'
      })
      .select('id, total_amount, guest_email, guest_details')
      .single();

    if (orderError) {
      console.error('Error creating order:', orderError);
      // Check for specific database errors
      if (orderError.code === '23505') {
        throw new Error('En order med samma information finns redan');
      } else if (orderError.code === '23503') {
        throw new Error('Ogiltig kundreferens');
      } else {
        throw new Error('Det gick inte att skapa ordern. Vänligen försök igen.');
      }
    }
    if (!orderData) throw new Error('Det gick inte att skapa ordern. Vänligen försök igen.');
    
    const orderId = orderData.id;
    console.log(`Created order ${orderId} with details:`, {
      total: orderData.total_amount,
      guest_email: orderData.guest_email,
      has_guest_details: !!orderData.guest_details
    });

    // Track the actual total based on items
    let actualTotal = 0;
    let failedItems = [];
    let successfulItems = [];
    
    // 2. Create order items and update inventory
    for (const item of p_items) {
      try {
        // Ensure unit_price is a valid number
        let unitPrice = Number(item.unit_price);
        if (isNaN(unitPrice) || unitPrice <= 0) {
          console.error('Invalid unit price for item:', item);
          // Try to fetch the current price from the product table
          const { data: productData } = await supabase
            .from('products')
            .select('price, name, stock_level')
            .eq('id', item.product_id)
            .single();
            
          if (productData && productData.price) {
            console.log('Using product price from database:', productData.price);
            unitPrice = Number(productData.price);
          } else {
            console.warn(`Skipping item ${item.product_id} due to invalid price`);
            failedItems.push(item.product_id);
            continue; // Skip this item
          }
        }
        
        // Round to 2 decimal places for currency
        unitPrice = Number(unitPrice.toFixed(2));
        const itemQuantity = Math.max(1, Number(item.quantity) || 1);
        
        // Update inventory first
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('stock_level')
          .eq('id', item.product_id)
          .single();

        if (productError || !productData) {
          console.error('Error fetching product stock_level:', productError);
          failedItems.push(item.product_id);
          continue;
        }

        // Double-check inventory again (in case it changed during order creation)
        if (productData.stock_level < itemQuantity) {
          console.error('Insufficient stock for product:', item.product_id);
          failedItems.push(item.product_id);
          continue;
        }

        // Note: We're removing the explicit inventory update here since it should be 
        // handled by database triggers when order_items are inserted
        // This prevents double reduction of inventory

        // Create order item
        const { error: itemError } = await supabase
          .from('order_items')
          .insert({
            order_id: orderId,
            product_id: item.product_id,
            quantity: itemQuantity,
            unit_price: unitPrice,
            price_at_time: unitPrice
          });
          
        if (itemError) {
          console.error('Error inserting order item:', itemError);
          // No need to restore stock level since we didn't update it directly
          failedItems.push(item.product_id);
          continue;
        }
        
        // Add to actual total only if item was successfully added
        actualTotal += unitPrice * itemQuantity;
        successfulItems.push(item.product_id);
      } catch (error) {
        console.error('Error processing item:', item, error);
        failedItems.push(item.product_id);
        continue;
      }
    }
    
    // If we couldn't add any items, delete the order and fail
    if (failedItems.length === p_items.length) {
      // Try to delete the order
      await supabase.from('orders').delete().eq('id', orderId);
      throw new Error('Det gick inte att lägga till några produkter i din order. Vänligen försök igen.');
    }
    
    // If some items failed, update the order with a note
    if (failedItems.length > 0) {
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          notes: `Vissa produkter kunde inte läggas till: ${failedItems.join(', ')}`,
          total_amount: actualTotal
        })
        .eq('id', orderId);
        
      if (updateError) {
        console.warn('Error updating order with failed items note:', updateError);
      }
    }
    
    // Now that order items are created, send the confirmation email
    try {
      console.log('Fetching complete order data for email...');
      const { data: completeOrderData, error: fetchError } = await supabase
        .from('orders')
        .select(`
          *,
          profiles:profiles(*),
          order_items:order_items(
            *,
            product:products(*)
          )
        `)
        .eq('id', orderId)
        .single();

      if (fetchError) {
        console.error('Failed to fetch complete order data for email:', fetchError);
        return orderId;
      }

      if (!completeOrderData) {
        console.error('No order data found for email sending');
        return orderId;
      }

      // Ensure the email is available in the complete order data
      let emailForSending = null;
      
      // Check if guest_email is available in the fetched order data
      if (completeOrderData.guest_email) {
        emailForSending = completeOrderData.guest_email;
        console.log('Using guest_email from fetched order:', emailForSending);
      } 
      // Check profile email if customer_id is set
      else if (completeOrderData.customer_id && completeOrderData.profiles) {
        // Profiles is an object, not an array - access properties directly
        const profileData = completeOrderData.profiles;
        if (profileData && profileData.email) {
          emailForSending = profileData.email;
          console.log('Using profile email from fetched order:', emailForSending);
        }
      } 
      // Use the email we had during order creation as fallback
      else if (customerId ? (guestDetails && guestDetails.email) : guestEmailForOrder) {
        emailForSending = customerId ? (guestDetails && guestDetails.email) : guestEmailForOrder;
        console.log('Using fallback email from order creation:', emailForSending);
        
        // Update the order with this email if it's missing
        if (!completeOrderData.guest_email && !customerId) {
          console.log('Updating order with missing guest_email:', emailForSending);
          await supabase
            .from('orders')
            .update({ guest_email: emailForSending })
            .eq('id', orderId);
        }
      }
      
      if (!emailForSending && !completeOrderData.customer_id) {
        console.error('Unable to find any email for sending confirmation:', {
          id: orderId,
          has_guest_email: !!completeOrderData.guest_email,
          has_customer_id: !!completeOrderData.customer_id,
          has_profile: !!completeOrderData.profiles,
          has_guest_details: !!completeOrderData.guest_details,
        });
      }
      
      // If we found an email but it's not in the order data, add it directly
      // This ensures the email service will be able to find it
      if (emailForSending && !completeOrderData.guest_email && !completeOrderData.customer_id) {
        console.log('Adding missing email directly to order data for email sending');
        completeOrderData.guest_email = emailForSending;
      }
      
      // Fix for email service - map 'profiles' to 'profile' since email service expects 'profile'
      if (completeOrderData.profiles && !completeOrderData.profile) {
        console.log('Setting profile property for email compatibility');
        completeOrderData.profile = completeOrderData.profiles;
      }
      
      console.log('Sending order confirmation email for order:', {
        orderId,
        email: emailForSending || 'MISSING EMAIL',
        hasOrderItems: completeOrderData.order_items?.length > 0,
        hasCustomerId: !!completeOrderData.customer_id,
        hasGuestEmail: !!completeOrderData.guest_email,
        hasProfile: !!completeOrderData.profile
      });

      // Comment out this function call to prevent duplicate emails
      // await sendOrderConfirmationEmail(completeOrderData);
    } catch (emailError) {
      console.error('Failed to send order confirmation email:', emailError);
      // Don't throw here - we don't want to roll back the order if email fails
    }
    
    // Return the order ID if we were able to add at least some items
    return orderId;
  } catch (error) {
    console.error('Error creating order directly:', error);
    // Throw a user-friendly error message
    if (error instanceof Error) {
      throw new Error(error.message || 'Det gick inte att skapa din order. Vänligen försök igen.');
    }
    throw new Error('Det gick inte att skapa din order. Vänligen försök igen.');
  }
}

// Send order confirmation email
const sendOrderConfirmationEmail = async (order: Order) => {
  try {
    const result = await sendUnifiedEmail(order);
    if (!result.success) {
      console.error('Failed to send order confirmation email:', result.error);
    }
  } catch (error) {
    console.error('Failed to send order confirmation email:', error);
  }
};

// Send status update email
const sendStatusUpdateEmail = async (order: Order) => {
  try {
    const result = await sendUnifiedEmail(order);
    if (!result.success) {
      console.error('Failed to send status update email:', result.error);
    }
  } catch (error) {
    console.error('Failed to send status update email:', error);
  }
};

// Update order status (admin only)
export async function updateOrderStatus(orderId: string, newStatus: string, userEmail: string) {
  try {
    // Update order status
    const { data: updatedOrder, error: updateError } = await supabase
      .from('orders')
      .update({ status: newStatus })
      .eq('id', orderId)
      .select(`
        *,
        profiles:profiles(*),
        order_items:order_items(
          *,
          product:products(*)
        )
      `)
      .single();

    if (updateError) throw updateError;
    if (!updatedOrder) throw new Error('Failed to update order');

    // Add to order history
    await supabase
      .from('order_history')
      .insert({
        order_id: orderId,
        status: newStatus,
        notes: `Status updated to ${newStatus}`,
        user_email: userEmail
      });

    // Send email notification using new unified service
    if (updatedOrder) {
      await sendStatusUpdateEmail(updatedOrder);
    }

    return updatedOrder;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
}

// Helper to recalculate an order's total after changes
async function recalculateOrderTotal(orderId: string) {
  try {
    console.log(`Recalculating total for order ${orderId}`);
    
    // Get all items for this order with their unit_price
    const { data: items, error: itemsError } = await supabase
      .from('order_items')
      .select('quantity, unit_price, product_id')
      .eq('order_id', orderId);
      
    if (itemsError) throw itemsError;
    console.log('Order items:', items);
    
    if (!items || items.length === 0) {
      console.warn('No items found for order');
      return 0;
    }
    
    // Calculate using the unit_price stored with each order item
    let newTotal = 0;
    
    for (const item of items) {
      const itemPrice = Number(item.unit_price);
      if (isNaN(itemPrice) || itemPrice <= 0) {
        console.warn(`Invalid unit_price for item: ${item.product_id}`, item.unit_price);
        
        // Try to get price from products table as fallback
        const { data: productData } = await supabase
          .from('products')
          .select('price')
          .eq('id', item.product_id)
          .single();
          
        if (productData && productData.price > 0) {
          console.log(`Using product price from database: ${productData.price}`);
          
          // Update the order item with the correct price
          await supabase
            .from('order_items')
            .update({ unit_price: productData.price })
            .eq('product_id', item.product_id)
            .eq('order_id', orderId);
            
          newTotal += item.quantity * productData.price;
        } else {
          console.warn(`Could not find valid price for product: ${item.product_id}`);
        }
      } else {
        newTotal += item.quantity * itemPrice;
      }
    }
    
    console.log(`Calculated new total: ${newTotal}`);
    
    // Update the order total
    const { error: updateError } = await supabase
      .from('orders')
      .update({ total_amount: newTotal })
      .eq('id', orderId);
      
    if (updateError) {
      console.error('Error updating order total:', updateError);
      throw updateError;
    }
    
    console.log(`Updated order ${orderId} total to ${newTotal}`);
    return newTotal;
  } catch (error) {
    console.error('Error recalculating order total:', error);
    throw error;
  }
}

// Helper function to send email notification for order status change
async function sendOrderStatusEmail(orderDetails: any, status: string): Promise<boolean> {
  try {
    // Skip sending emails for 'new' status or if no valid template
    if (status === 'new') return true;
    
    // Get the appropriate email template (custom or default)
    const emailTemplate = getTemplate(status, orderDetails);
    if (!emailTemplate) {
      console.log(`No email template found for status: ${status}`);
      return false;
    }
    
    // More detailed logging for debugging email issues
    console.log('Order details for email:', {
      id: orderDetails.id,
      status,
      hasProfile: !!orderDetails.profile,
      profileEmail: orderDetails.profile?.email,
      guestEmail: orderDetails.guest_email,
      guestDetails: orderDetails.guest_details
    });
    
    // Try to get customer email from multiple possible sources
    let customerEmail = null;
    
    // Check profile email first
    if (orderDetails.profile && orderDetails.profile.email) {
      customerEmail = orderDetails.profile.email;
      console.log(`Using profile email: ${customerEmail}`);
    } 
    // Then check guest_email
    else if (orderDetails.guest_email) {
      customerEmail = orderDetails.guest_email;
      console.log(`Using guest email: ${customerEmail}`);
    } 
    // Finally try to extract from guest_details as a last resort
    else if (orderDetails.guest_details) {
      let guestDetails = orderDetails.guest_details;
      
      // Parse guest details if it's a string
      if (typeof guestDetails === 'string') {
        try {
          guestDetails = JSON.parse(guestDetails);
        } catch (e) {
          console.error('Error parsing guest details:', e);
        }
      }
      
      // Try to find email in the parsed object
      if (guestDetails && typeof guestDetails === 'object') {
        customerEmail = guestDetails.email || guestDetails.Email || guestDetails.emailAddress || guestDetails.EMAIL;
        if (customerEmail) {
          console.log(`Found email in guest details: ${customerEmail}`);
        }
      }
    }
    
    if (!customerEmail) {
      console.error('No customer email found for order:', orderDetails.id);
      // Return true to prevent the function from failing even if we can't send an email
      return true;
    }
    
    console.log(`Sending ${status} email notification to ${customerEmail} for order ${orderDetails.id}`);
    
    // In a real implementation, you would use your email service here
    // For now, we'll just log it for testing
    /*
    Example with a hypothetical email service:
    
    await emailService.send({
      to: customerEmail,
      subject: `Din beställning har ändrat status till ${getStatusName(status)}`,
      html: emailTemplate
    });
    */
    
    // For testing - log a snippet of the template to see if it's rendering correctly
    console.log('Email template preview:');
    if (emailTemplate.length > 500) {
      console.log(emailTemplate.substring(0, 500) + '... [truncated]');
    } else {
      console.log(emailTemplate);
    }
    
    // Log template success
    console.log(`✓ Email template for status "${status}" generated successfully`);
    
    return true;
  } catch (error) {
    console.error('Error sending order status email:', error);
    // Return true to prevent the function from failing the order update operation
    return true;
  }
}

// Helper function to get a user-friendly status name
function getStatusName(status: string): string {
  const statusMap: Record<string, string> = {
    'pending': 'Väntar',
    'confirmed': 'Bekräftad',
    'preparing': 'Förbereds',
    'out_for_delivery': 'Under leverans',
    'delivered': 'Levererad',
    'cancelled': 'Avbruten'
  };
  
  return statusMap[status] || status;
}

// Update entire order (admin only)
export async function updateOrder(
  orderId: string, 
  status: string | null, 
  items: {id?: string, product_id: string, quantity: number}[] | null,
  profile: Profile | null = null,
  adminEmail: string = 'admin',
  deliveryInfo: {
    delivery_date?: string,
    delivery_time?: string,
    delivery_notes?: string,
    address?: string,
    guest_email?: string,
    guest_details?: any
  } | null = null
) {
  try {
    // Log the status value for debugging
    console.log(`Updating order ${orderId} with status:`, status);
    
    // DIRECT FIX FOR PACKED: Replace with a correct hardcoded string without spaces
    if (status && (status.includes('pack') || status === 'packe d')) {
      const fixedPackedStatus = 'preparing'; // Map to a valid enum value in the database
      console.log(`Replacing status "${status}" with valid fixed status "${fixedPackedStatus}"`);
      status = fixedPackedStatus;
    }
    
    // Skip updating if status is null or undefined
    if (status === null || status === undefined) {
      console.log('Status is null or undefined, skipping status update');
    } else {
      // Validate status against known good values
      const validStatuses = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];
      if (!validStatuses.includes(status)) {
        console.warn(`Warning: Status "${status}" is not in the list of known valid values`);
        
        // Find closest match
        for (const valid of validStatuses) {
          if (valid.startsWith(status) || status.startsWith(valid)) {
            console.log(`Mapping "${status}" to valid value "${valid}"`);
            status = valid;
            break;
          }
        }
        
        // Still not valid? Default to "pending"
        if (!validStatuses.includes(status)) {
          console.warn(`No match found for "${status}", defaulting to "pending"`);
          status = 'pending';
        }
      }
    }
    
    // Fetch order before update to check if status is changing
    let originalOrder = null;
    if (status) {
      try {
        originalOrder = await getOrder(orderId);
        console.log(`Original order status: ${originalOrder?.status}, New status: ${status}`);
      } catch (err) {
        console.warn('Could not fetch original order status:', err);
      }
    }
    
    // Use direct table operations for more reliable updates
    
    // 1. Update status if provided
    if (status) {
      console.log(`Directly updating order status to: ${status}`);
      const { error: statusError } = await supabase
        .from('orders')
        .update({ status })
        .eq('id', orderId);
        
      if (statusError) {
        console.error('Error updating status:', statusError);
        throw statusError;
      }
      
      // Add entry to order history for status change
      const { error: historyError } = await supabase
        .from('order_history')
        .insert({
          order_id: orderId,
          status,
          notes: `Status updated to ${status}`,
          user_email: adminEmail
        });
        
      if (historyError) {
        console.warn('Failed to add history record for status change:', historyError);
      }
    }
    
    // 2. Update delivery information if provided
    if (deliveryInfo) {
      const updates: any = {};
      
      if (deliveryInfo.delivery_date) updates.delivery_date = deliveryInfo.delivery_date;
      if (deliveryInfo.delivery_time) updates.delivery_time = deliveryInfo.delivery_time;
      if (deliveryInfo.delivery_notes) updates.delivery_notes = deliveryInfo.delivery_notes;
      
      // Handle delivery address
      if (deliveryInfo.address) {
        updates.address = deliveryInfo.address;
        console.log('Setting delivery address:', deliveryInfo.address);
      }
      
      // Handle guest email updates
      if (deliveryInfo.guest_email) {
        updates.guest_email = deliveryInfo.guest_email;
        console.log('Setting guest email:', deliveryInfo.guest_email);
      }
      
      // Handle guest details
      if (deliveryInfo.guest_details) {
        // If guest_details is a string, try to parse it
        let guestDetails = deliveryInfo.guest_details;
        if (typeof guestDetails === 'string') {
          try {
            guestDetails = JSON.parse(guestDetails);
          } catch (e) {
            console.error('Error parsing guest details:', e);
          }
        }
        
        // Ensure guestDetails is an object before storing
        if (typeof guestDetails === 'object' && guestDetails !== null) {
          updates.guest_details = guestDetails;
          console.log('Setting guest details:', guestDetails);
        } else {
          updates.guest_details = deliveryInfo.guest_details;
        }
      }
      
      if (Object.keys(updates).length > 0) {
        const { error: updateError } = await supabase
          .from('orders')
          .update(updates)
          .eq('id', orderId);
          
        if (updateError) {
          console.warn('Failed to update delivery/guest info:', updateError);
        } else {
          // Add entry to order history
          const { error: historyError } = await supabase
            .from('order_history')
            .insert({
              order_id: orderId,
              status: 'updated',
              notes: `Delivery/customer information updated`,
              user_email: adminEmail
            });
            
          if (historyError) {
            console.warn('Failed to add history record for delivery update:', historyError);
          }
        }
      }
    }
    
    // 3. Update profile if provided
    if (profile) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
          phone: profile.phone,
          address: profile.address
        })
        .eq('id', profile.id);
        
      if (profileError) throw profileError;
    }
    
    // 4. Update items if provided
    if (items && items.length > 0) {
      // Get existing items
      const { data: existingItems, error: fetchError } = await supabase
        .from('order_items')
        .select('id, product_id, quantity')
        .eq('order_id', orderId);
        
      if (fetchError) throw fetchError;
      
      // Create a map of existing items by ID
      const existingItemMap = new Map();
      existingItems.forEach(item => {
        existingItemMap.set(item.id, item);
      });
      
      // Process each item in the update
      for (const item of items) {
        if (item.id) {
          // Update existing item
          const { error: updateError } = await supabase
            .from('order_items')
            .update({ quantity: item.quantity })
            .eq('id', item.id);
            
          if (updateError) throw updateError;
          
          // Remove from map to track what's been processed
          existingItemMap.delete(item.id);
        } else {
          // Add new item - first get current product price
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('price')
            .eq('id', item.product_id)
            .single();
            
          if (productError) throw productError;
          if (!productData) throw new Error(`Product ${item.product_id} not found`);
          
          const { error: insertError } = await supabase
            .from('order_items')
            .insert({
              order_id: orderId,
              product_id: item.product_id,
              quantity: item.quantity,
              unit_price: productData.price,
              price_at_time: productData.price
            });
            
          if (insertError) throw insertError;
        }
      }
      
      // Any items left in the map need to be deleted (they weren't in the update)
      for (const [itemId, item] of existingItemMap.entries()) {
        const { error: deleteError } = await supabase
          .from('order_items')
          .delete()
          .eq('id', itemId);
          
        if (deleteError) throw deleteError;
      }
      
      // Recalculate order total
      await recalculateOrderTotal(orderId);
    }
    
    // Try RPC call as a fallback for legacy compatibility
    try {
      console.log('Attempting RPC update_order call for compatibility');
      await supabase.rpc('update_order', {
        p_order_id: orderId,
        p_status: status,
        p_items: items ? JSON.stringify(items) : null,
        p_profile: profile ? JSON.stringify(profile) : null,
        p_admin_email: adminEmail
      });
    } catch (rpcError) {
      console.warn('RPC update_order failed, but direct updates succeeded:', rpcError);
    }
    
    // Fetch the updated order
    const updatedOrder = await getOrder(orderId);
    
    // Send email notification if status has changed
    if (status && originalOrder && originalOrder.status !== status) {
      await sendOrderStatusEmail(updatedOrder, status);
    }
    
    return updatedOrder;
  } catch (error) {
    console.error('Error updating order:', error);
    throw error;
  }
}

// Helper function to restore product inventory
// Note: This function should only be used when database triggers cannot handle inventory restoration
async function restoreProductInventory(orderItems: { product_id: string, quantity: number }[]) {
  console.log('Using backup method to restore inventory for items:', orderItems);
  
  for (const item of orderItems) {
    try {
      // Log the inventory restoration attempt
      console.log(`Manual inventory restoration for product ${item.product_id} (${item.quantity} units)`);
      
      // Get current stock level
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('stock_level')
        .eq('id', item.product_id)
        .single();

      if (productError) {
        console.error('Error fetching product stock level:', productError);
        continue;
      }

      // Calculate new stock level
      const newStockLevel = (productData.stock_level || 0) + item.quantity;

      // Update stock level
      const { error: updateError } = await supabase
        .from('products')
        .update({ stock_level: newStockLevel })
        .eq('id', item.product_id);

      if (updateError) {
        console.error('Error restoring product stock level:', updateError);
      } else {
        console.log(`Restored ${item.quantity} units to product ${item.product_id}`);
      }
    } catch (error) {
      console.error('Error in restoreProductInventory:', error);
    }
  }
}

// Update the removeOrderItem function to let database triggers handle inventory
export async function removeOrderItem(itemId: string, adminEmail: string = 'admin') {
  try {
    // Get the order item details before deletion
    const { data: itemData, error: itemFetchError } = await supabase
      .from('order_items')
      .select('order_id, product_id, quantity')
      .eq('id', itemId)
      .single();
        
    if (itemFetchError) throw itemFetchError;
    if (!itemData) throw new Error('Order item not found');
      
    const orderId = itemData.order_id;
    
    // Note: We'll let database triggers handle inventory restoration
    // by setting the order status to 'cancelled' before deletion
      
    // Delete the item
    const { error: deleteError } = await supabase
      .from('order_items')
      .delete()
      .eq('id', itemId);
        
    if (deleteError) throw deleteError;
      
    // Add entry to order history
    const { error: historyError } = await supabase
      .from('order_history')
      .insert({
        order_id: orderId,
        status: 'updated',
        notes: `Item ${itemData.product_id} (qty: ${itemData.quantity}) removed from order`,
        user_email: adminEmail
      });
        
    if (historyError) {
      console.warn('Failed to add history record, but item was deleted:', historyError);
    }
      
    // Recalculate order total
    await recalculateOrderTotal(orderId);
      
    return true;
  } catch (error) {
    console.error('Error removing order item:', error);
    throw error;
  }
}

// Update the handleDeleteOrder function to let database triggers handle inventory
export async function handleDeleteOrder(orderId: string) {
  try {
    console.log(`Starting deletion of order ${orderId}`);
    
    // First check if this order exists and get its status
    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .select('status')
      .eq('id', orderId)
      .single();
      
    if (orderError) {
      console.error('Error fetching order:', orderError);
      throw orderError;
    }
    
    if (!orderData) {
      console.error('Order not found:', orderId);
      throw new Error('Order not found');
    }
    
    console.log(`Order status: ${orderData.status}`);
    
    // Note: We'll let database triggers handle inventory restoration
    // by setting the order status to 'cancelled' before deletion
    if (orderData.status !== 'cancelled') {
      console.log('Changing order status to cancelled before deletion to trigger inventory restoration');
      const { error: updateError } = await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', orderId);
        
      if (updateError) {
        console.error('Error updating order status:', updateError);
        // Continue with deletion anyway
      }
    }

    // Delete related records in order
    console.log('Deleting related records...');

    // 1. Delete order items
    const { error: itemsError } = await supabase
      .from('order_items')
      .delete()
      .eq('order_id', orderId);
      
    if (itemsError) {
      console.error('Error deleting order items:', itemsError);
      throw itemsError;
    }
    console.log('Order items deleted');

    // 2. Delete order history
    const { error: historyError } = await supabase
      .from('order_history')
      .delete()
      .eq('order_id', orderId);
      
    if (historyError) {
      console.error('Error deleting order history:', historyError);
      throw historyError;
    }
    console.log('Order history deleted');

    // 3. Delete email queue and logs
    const { data: queueData } = await supabase
      .from('order_email_queue')
      .select('id')
      .eq('order_id', orderId);
      
    if (queueData && queueData.length > 0) {
      const queueIds = queueData.map(item => item.id);
      
      // Delete email logs first
      const { error: logsError } = await supabase
        .from('order_email_logs')
        .delete()
        .in('queue_id', queueIds);
        
      if (logsError) {
        console.error('Error deleting email logs:', logsError);
      }
      
      // Then delete queue entries
      const { error: queueError } = await supabase
        .from('order_email_queue')
        .delete()
        .eq('order_id', orderId);
        
      if (queueError) {
        console.error('Error deleting email queue:', queueError);
      }
    }
    console.log('Email records deleted');

    // 4. Finally delete the order itself
    const { error: deleteOrderError } = await supabase
      .from('orders')
      .delete()
      .eq('id', orderId);
      
    if (deleteOrderError) {
      console.error('Error deleting order:', deleteOrderError);
      throw deleteOrderError;
    }
    console.log('Order deleted successfully');

    return true;
  } catch (error) {
    console.error('Error in handleDeleteOrder:', error);
    throw error;
  }
}

// Update deleteAllOrders to restore inventory
export async function deleteAllOrders() {
  try {
    console.log('Starting deletion of all orders...');
    
    // Get all order items first to restore inventory
    const { data: allOrderItems, error: itemsFetchError } = await supabase
      .from('order_items')
      .select('product_id, quantity');
      
    if (itemsFetchError) {
      console.error('Error fetching order items:', itemsFetchError);
      throw itemsFetchError;
    }

    if (allOrderItems && allOrderItems.length > 0) {
      // Restore inventory for all items
      await restoreProductInventory(allOrderItems);
    }
    
    // Delete all order_items
    const { error: itemsError } = await supabase
      .from('order_items')
      .delete()
      .filter('id', 'not.is', null);
      
    if (itemsError) {
      console.error('Error deleting order items:', itemsError);
      throw itemsError;
    }
    console.log('Successfully deleted all order items');
    
    // Then delete all order_history
    const { error: historyError } = await supabase
      .from('order_history')
      .delete()
      .filter('id', 'not.is', null);
      
    if (historyError) {
      console.error('Error deleting order history:', historyError);
      throw historyError;
    }
    console.log('Successfully deleted all order history');
    
    // Finally delete all orders
    const { error: ordersError } = await supabase
      .from('orders')
      .delete()
      .filter('id', 'not.is', null);
      
    if (ordersError) {
      console.error('Error deleting orders:', ordersError);
      throw ordersError;
    }
    console.log('Successfully deleted all orders');
    
    return { success: true, message: 'All orders have been deleted and inventory restored' };
  } catch (error) {
    console.error('Error in deleteAllOrders:', error);
    throw error;
  }
}

// Test valid order statuses
export async function testOrderStatuses() {
  console.log('Testing order statuses...');
  
  // Get database valid order statuses
  try {
    // Try to query a specific table in PostgreSQL that holds enum values
    const { data: enumValues, error: enumError } = await supabase.rpc(
      'get_enum_values',
      { enum_name: 'order_status' }
    );
    
    if (enumError) {
      console.error('Error fetching enum values:', enumError);
      
      // Fallback approach - create a temporary order with each status to test
      const testStatuses = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];
      const results: Record<string, string> = {};
      
      for (const status of testStatuses) {
        try {
          console.log(`Testing status value: "${status}"`);
          
          // Try to update a test order with this status
          const { error } = await supabase
            .from('orders')
            .update({ status })
            .eq('id', 'test-status-placeholder')
            .limit(1);
            
          // Record if this is a valid status (based on error type)
          results[status] = error ? 
            (error.code === '22P02' ? 'Invalid enum value' : 'Other error') : 
            'Valid';
            
          console.log(`Status "${status}" result:`, results[status]);
        } catch (error) {
          console.error(`Error testing status "${status}":`, error);
          results[status] = 'Error';
        }
      }
      
      console.log('Status test results:', results);
      return { success: false, error: enumError, testResults: results };
    }
    
    console.log('Valid order statuses from database:', enumValues);
    return { success: true, statuses: enumValues };
  } catch (error) {
    console.error('Error testing order statuses:', error);
    return { success: false, error };
  }
}

/**
 * Validates order status changes by testing each valid status on a test order
 * @returns Test results for each status change
 */
export async function validateOrderStatusChanges() {
  const validStatuses = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];
  const results: Record<string, { success: boolean, error?: any }> = {};
  
  try {
    // Get a test order or create one if needed
    const { data: testOrders, error: fetchError } = await supabase
      .from('orders')
      .select('id')
      .limit(1);
      
    if (fetchError || !testOrders || testOrders.length === 0) {
      return { success: false, error: 'No orders found to test with' };
    }
    
    const testOrderId = testOrders[0].id;
    console.log(`Using order ${testOrderId} for status validation`);
    
    // Test each status
    for (const status of validStatuses) {
      try {
        console.log(`Testing status change to: ${status}`);
        const result = await updateOrderStatus(testOrderId, status, 'validator@test.com');
        
        // Check if the update was successful
        const updatedOrder = await getOrder(testOrderId);
        
        if (updatedOrder && updatedOrder.status === status) {
          results[status] = { success: true };
          console.log(`✓ Status change to ${status} successful`);
        } else {
          results[status] = { 
            success: false, 
            error: `Status not updated correctly. Expected: ${status}, Got: ${updatedOrder?.status}` 
          };
          console.warn(`✗ Status change to ${status} failed: ${updatedOrder?.status}`);
        }
      } catch (error) {
        results[status] = { success: false, error };
        console.error(`✗ Error changing status to ${status}:`, error);
      }
    }
    
    return { 
      success: Object.values(results).every(r => r.success), 
      results,
      summary: `${Object.values(results).filter(r => r.success).length}/${validStatuses.length} status changes successful` 
    };
  } catch (error) {
    console.error('Error validating order status changes:', error);
    return { success: false, error };
  }
}

/**
 * A simplified function to explicitly test order status updates
 * Avoids any complications with the RPC method
 */
export async function testSimpleStatusUpdate(orderId: string, status: string) {
  console.log(`Testing simple status update for order ${orderId} to status: ${status}`);
  
  try {
    // First verify if the status is valid
    const validStatuses = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];
    
    if (!validStatuses.includes(status)) {
      console.warn(`Warning: Status "${status}" is invalid. Must be one of: ${validStatuses.join(', ')}`);
      return { success: false, error: 'Invalid status value' };
    }
    
    // Get the original order status
    const originalOrder = await getOrder(orderId);
    console.log(`Original status: ${originalOrder?.status}`);
    
    // Perform direct update with minimum complexity
    const { error } = await supabase
      .from('orders')
      .update({ status })
      .eq('id', orderId);
    
    if (error) {
      console.error('Status update failed:', error);
      return { success: false, error };
    }
    
    // Verify the update
    const updatedOrder = await getOrder(orderId);
    console.log(`Updated status: ${updatedOrder?.status}`);
    
    const success = updatedOrder?.status === status;
    
    return { 
      success, 
      originalStatus: originalOrder?.status,
      newStatus: updatedOrder?.status,
      message: success ? 'Status updated successfully' : 'Status not updated correctly'
    };
  } catch (error) {
    console.error('Error in test status update:', error);
    return { success: false, error };
  }
}

/**
 * Test function to verify all email templates
 * @returns Results for each template test
 */
export async function testAllOrderEmailTemplates() {
  type TemplateTestResult = { 
    success: boolean;
    error?: any;
    source?: string;
  };
  
  const validStatuses = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];
  const results: Record<string, TemplateTestResult> = {};
  
  try {
    // Get a test order to use for template generation
    const { data: testOrders, error: fetchError } = await supabase
      .from('orders')
      .select(`
        *,
        profiles:profiles(*),
        order_items:order_items(
          *,
          product:products(*)
        )
      `)
      .limit(1);
      
    if (fetchError || !testOrders || testOrders.length === 0) {
      return { 
        success: false, 
        error: 'No orders found to test with',
        message: 'Create at least one order with items to test email templates'
      };
    }
    
    const testOrder = testOrders[0];
    console.log(`Using order ${testOrder.id} for email template testing`);
    
    // Enhance the test order with delivery information if not present
    const testOrderWithDelivery = {
      ...testOrder,
      delivery_date: testOrder.delivery_date || new Date().toISOString().split('T')[0],
      delivery_time: testOrder.delivery_time || '14:00-16:00',
      delivery_notes: testOrder.delivery_notes || 'Please leave package at the door',
      address: testOrder.address || 'Storgatan 1, 123 45 Stockholm'
    };
    
    // Import the function to get database templates if available
    const { getEmailTemplateByStatus } = await import('./orderEmails');
    
    // Test each status template
    for (const status of validStatuses) {
      try {
        console.log(`Testing email template for status: ${status}`);
        
        // First check for a database template
        const dbTemplate = await getEmailTemplateByStatus(status);
        let emailHtml = null;
        let templateSource = '';
        
        if (dbTemplate && dbTemplate.body) {
          // Use database template
          emailHtml = dbTemplate.body;
          templateSource = 'database';
        } else {
          // Fall back to default template
          emailHtml = getOrderStatusEmailTemplate(status, testOrderWithDelivery);
          templateSource = 'default';
        }
        
        if (!emailHtml) {
          results[status] = { success: false, error: 'No template found for this status' };
          console.error(`✗ No template found for status "${status}"`);
        } else {
          // Check if template contains expected elements
          let isValid = true;
          let missingElements = [];
          
          // Database templates might have different structure, so check accordingly
          if (templateSource === 'default') {
            const hasTitle = emailHtml.includes(`<title>`);
            const hasOrderItems = emailHtml.includes(`<tbody>`);
            const hasStatusBadge = emailHtml.includes(`status-badge`);
            const hasDeliveryInfo = emailHtml.includes(`Leveransinformation`);
            
            isValid = hasTitle && hasOrderItems;
            
            if (!hasTitle) missingElements.push('title');
            if (!hasOrderItems) missingElements.push('order items');
            if (!hasStatusBadge) missingElements.push('status badge');
            if (!hasDeliveryInfo) missingElements.push('delivery info');
          } else {
            // For database templates, just check if it has some common HTML structure
            const hasHtml = emailHtml.includes(`<html`) || emailHtml.includes(`<body`);
            const hasParagraph = emailHtml.includes(`<p`) || emailHtml.includes(`<div`);
            
            isValid = hasHtml && hasParagraph;
            
            if (!hasHtml) missingElements.push('HTML structure');
            if (!hasParagraph) missingElements.push('content elements');
          }
          
          if (isValid) {
            results[status] = { success: true, source: templateSource };
            console.log(`✓ Template for "${status}" generated successfully (${templateSource})`);
          } else {
            results[status] = { 
              success: false, 
              error: `Template missing required elements: ${missingElements.join(', ')}`,
              source: templateSource
            };
            console.warn(`✗ Template for "${status}" is missing required elements`);
          }
        }
      } catch (error) {
        results[status] = { success: false, error };
        console.error(`✗ Error generating template for status "${status}":`, error);
      }
    }
    
    // Count successful templates
    const successCount = Object.values(results).filter(r => r.success).length;
    const dbTemplateCount = Object.values(results).filter(r => r.source === 'database').length;
    
    return { 
      success: successCount === validStatuses.length,
      results,
      summary: `${successCount}/${validStatuses.length} email templates verified successfully (${dbTemplateCount} from database)`
    };
  } catch (error) {
    console.error('Error testing email templates:', error);
    return { success: false, error };
  }
}

// Test function to verify email sending fix
export async function testEmailFix(orderId?: string) {
  try {
    console.log('Testing email fix...');
    
    // If no order ID provided, fetch the most recent order
    if (!orderId) {
      const { data: recentOrder, error } = await supabase
        .from('orders')
        .select('id')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (error || !recentOrder) {
        console.error('Could not find a recent order to test with:', error);
        return { success: false, error: 'No recent order found' };
      }
      
      orderId = recentOrder.id;
    }
    
    console.log(`Testing with order ID: ${orderId}`);
    
    // Fetch the complete order data
    const { data: completeOrderData, error: fetchError } = await supabase
      .from('orders')
      .select(`
        *,
        profiles:profiles(*),
        order_items:order_items(
          *,
          product:products(*)
        )
      `)
      .eq('id', orderId)
      .single();
      
    if (fetchError || !completeOrderData) {
      console.error('Error fetching order data:', fetchError);
      return { success: false, error: fetchError };
    }
    
    // Log the current email-related information
    console.log('Order data for email testing:', {
      order_id: completeOrderData.id,
      has_profiles: !!completeOrderData.profiles,
      profiles_data: completeOrderData.profiles,
      guest_email: completeOrderData.guest_email,
      has_guest_details: !!completeOrderData.guest_details
    });
    
    // Apply our fix
    if (completeOrderData.profiles && !completeOrderData.profile) {
      console.log('Setting profile property for email compatibility');
      completeOrderData.profile = completeOrderData.profiles;
    }
    
    // Attempt to send the email
    console.log('Attempting to send order confirmation email...');
    const result = await sendOrderConfirmationEmail(completeOrderData);
    
    console.log('Email sending result:', result);
    return { success: true, result };
  } catch (error) {
    console.error('Error testing email fix:', error);
    return { success: false, error };
  }
}

// Fix inventory update trigger function
export async function fixInventoryUpdateTrigger() {
  try {
    const { error } = await supabase.rpc('fix_inventory_update_trigger');
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error fixing inventory update trigger:', error);
    throw error;
  }
} 